import React, { useState, useContext, useEffect, useMemo } from "react";
import { Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/Graphs/Dialogue";
import { Button } from "components/Graphs/Button";
import { Input } from "components/Graphs/Input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRoot,
  TableRow,
} from "components/Graphs/Table";
import CopyField from "components/UI-lib/CopyField";
import {
  NodeDetailSectionDescription,
  NodeDetailSectionHeader,
} from "components/NodeDetail/shared/NodeDetailSection";
import { OrgContext } from "contexts/OrgContext";
import { UserContext } from "contexts/UserContext";
import UsernameDisplayField, {
  EmailDisplayField,
  UsernameDisplayCircle,
} from "components/UsernameDisplayField";
import { EnableSSHBanner } from "./shared";
import { LinuxUserSetupInstructions } from "components/BrevConnect/LinuxUserSetupInstructions";
import { ENABLE_SSH_COMMAND } from "components/BrevConnect/DeviceConnected/utils";
import { isOrgAdminOrMaintainerRole } from "utils/roleUtils";
import { organizationAccessClient } from "server/devplane/OrganizationAccesses";
import { organizationAccessLevelToOrgRole } from "utils/organizationAccess";

// ─── add linux user dialog ────────────────────────────────────────────────────

const AddLinuxUserDialog: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
}> = ({ open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="lg:max-w-4xl sm:max-w-[calc(100vw-4rem)] overflow-hidden">
      <DialogHeader className="min-w-0">
        <DialogTitle className="text-2xl text-white font-bold">
          Add a Linux user from your device
        </DialogTitle>
        <DialogDescription className="text-neutral-400">
          Enable SSH access for a new Linux user on this node.
        </DialogDescription>
      </DialogHeader>
      <div className="min-w-0 space-y-4 py-4">
        <div>
          <p className="text-sm font-medium text-white">
            1. Confirm that the active Linux user on your device is the user you
            want to enable SSH access for.
          </p>
          <LinuxUserSetupInstructions
            idPrefix="add-linux-user"
            className="mt-3"
          />
        </div>
        <div>
          <p className="text-sm font-medium text-white">2. Enable SSH</p>
          <div className="mt-3 min-w-0 overflow-hidden">
            <CopyField
              value={ENABLE_SSH_COMMAND}
              analytics={{ userid: "", eventLabel: "brev enable-ssh" }}
              className="w-full"
            />
          </div>
        </div>
      </div>
      <DialogFooter className="min-w-0">
        <Button variant="secondary" onClick={() => onOpenChange(false)}>
          Done
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// ─── modify ssh access dialog ─────────────────────────────────────────────────

const ModifySSHAccessDialog: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  manageableLinuxUsers: string[];
  readOnlyLinuxUsers: string[];
  currentLinuxUsers: string[];
  onSave: (toGrant: string[], toRevoke: string[]) => Promise<void>;
}> = ({
  open,
  onOpenChange,
  manageableLinuxUsers,
  readOnlyLinuxUsers,
  currentLinuxUsers,
  onSave,
}) => {
  const [selected, setSelected] = useState<Set<string>>(
    new Set(currentLinuxUsers),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) setSelected(new Set(currentLinuxUsers));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasChanges = manageableLinuxUsers.some(
    (lu) => selected.has(lu) !== currentLinuxUsers.includes(lu),
  );

  const handleClose = () => onOpenChange(false);

  const handleToggle = (lu: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(lu)) next.delete(lu);
      else next.add(lu);
      return next;
    });
  };

  const handleSave = async () => {
    const original = new Set(currentLinuxUsers);
    const toGrant = manageableLinuxUsers.filter(
      (lu) => selected.has(lu) && !original.has(lu),
    );
    const toRevoke = manageableLinuxUsers.filter(
      (lu) => !selected.has(lu) && original.has(lu),
    );
    if (toGrant.length === 0 && toRevoke.length === 0) {
      handleClose();
      return;
    }
    setIsSubmitting(true);
    try {
      await onSave(toGrant, toRevoke);
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
    >
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader className="pb-2">
          <DialogTitle className="text-white">Modify SSH Access</DialogTitle>
          <DialogDescription className="text-neutral-400">
            <span className="block">
              Select the Linux users this person can log in as. Brev will SSH
              into the node using your keys to add their public key to each
              selected user&apos;s{" "}
              <code className="rounded bg-neutral-800 px-1 py-0.5 font-mono text-xs text-neutral-200">
                authorized_keys
              </code>
              .
            </span>
            <span className="block mt-2">
              To add a new Linux user, create it on the box and run{" "}
              <code className="rounded bg-neutral-800 px-1 py-0.5 font-mono text-xs text-neutral-200">
                {ENABLE_SSH_COMMAND}
              </code>{" "}
              as that user.
            </span>
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col divide-y divide-neutral-800 rounded-md border border-neutral-800 overflow-hidden">
          {manageableLinuxUsers.map((lu) => {
            const on = selected.has(lu);
            return (
              <button
                key={lu}
                type="button"
                onClick={() => handleToggle(lu)}
                className="flex items-center justify-between px-4 py-3 text-left hover:bg-neutral-800 transition-colors"
              >
                <span className="font-mono text-sm text-neutral-200">{lu}</span>
                <span className="flex items-center gap-1.5">
                  <span
                    className={`h-2 w-2 rounded-full ${on ? "bg-highlight" : "bg-neutral-600"}`}
                  />
                  <span
                    className={`text-xs font-medium ${on ? "text-highlight" : "text-neutral-500"}`}
                  >
                    {on ? "ON" : "OFF"}
                  </span>
                </span>
              </button>
            );
          })}
          {readOnlyLinuxUsers.map((lu) => (
            <div
              key={lu}
              className="flex items-center justify-between px-4 py-3 opacity-50 cursor-not-allowed"
              title="You don't have SSH access to this Linux user"
            >
              <span className="font-mono text-sm text-neutral-200">{lu}</span>
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-highlight" />
                <span className="text-xs font-medium text-highlight">ON</span>
              </span>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!hasChanges || isSubmitting}
            isLoading={isSubmitting}
            loadingText="Saving..."
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── ssh access section ───────────────────────────────────────────────────────

export interface NodeSSHAccessSectionProps {
  sshAccess: Array<{ userId: string; linuxUser: string }> | undefined;
  onGrant: (userId: string, linuxUser: string) => Promise<void>;
  onRevoke: (userId: string, linuxUser: string) => Promise<void>;
}

export const NodeSSHAccessSection: React.FC<NodeSSHAccessSectionProps> = ({
  sshAccess,
  onGrant,
  onRevoke,
}) => {
  const { activeOrgId, activeOrgRole } = useContext(OrgContext);
  const isAdminOrMaintainer = isOrgAdminOrMaintainerRole(activeOrgRole);
  const { me, getUser } = useContext(UserContext);
  const [members, setMembers] = useState<
    Array<{ userId: string; roleId: string; username?: string; email?: string }>
  >([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [modifyingUserId, setModifyingUserId] = useState<string | null>(null);
  const [addLinuxUserOpen, setAddLinuxUserOpen] = useState(false);

  useEffect(() => {
    if (!activeOrgId) return;
    setLoadingMembers(true);
    organizationAccessClient
      .listMembers(activeOrgId)
      .then(async (res) => {
        const resolved = await Promise.all(
          res.members.map(async (member) => {
            const user = await getUser(member.userId);
            return {
              userId: member.userId,
              roleId:
                organizationAccessLevelToOrgRole(member.accessLevel) ?? "",
              username: user?.username,
              email: user?.email,
            };
          }),
        );
        setMembers(resolved);
      })
      .catch((err) => console.error("Failed to load org members:", err))
      .finally(() => setLoadingMembers(false));
  }, [activeOrgId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Linux users the current user has been granted — they can only grant these to others
  const myLinuxUsers = useMemo(
    () =>
      sshAccess?.filter((a) => a.userId === me?.id).map((a) => a.linuxUser) ??
      [],
    [sshAccess, me?.id],
  );

  // Only org admins and maintainers can grant/revoke access
  const canManage = isAdminOrMaintainer && myLinuxUsers.length > 0;

  // Group sshAccess by userId → list of linux users
  const accessByUser = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const entry of sshAccess ?? []) {
      if (!map[entry.userId]) map[entry.userId] = [];
      map[entry.userId].push(entry.linuxUser);
    }
    return map;
  }, [sshAccess]);

  const filteredMembers = useMemo(() => {
    if (!searchTerm.trim()) return members;
    const lower = searchTerm.toLowerCase();
    return members.filter(
      (m) =>
        m.userId.toLowerCase().includes(lower) ||
        m.username?.toLowerCase().includes(lower) === true ||
        m.email?.toLowerCase().includes(lower) === true,
    );
  }, [members, searchTerm]);

  const handleModifyAccess = async (toGrant: string[], toRevoke: string[]) => {
    if (!modifyingUserId) return;
    setLoadingUsers((prev) => new Set([...prev, modifyingUserId]));
    try {
      await Promise.all([
        ...toGrant.map((lu) => onGrant(modifyingUserId, lu)),
        ...toRevoke.map((lu) => onRevoke(modifyingUserId, lu)),
      ]);
    } finally {
      setLoadingUsers((prev) => {
        const s = new Set(prev);
        s.delete(modifyingUserId);
        return s;
      });
    }
  };

  return (
    <>
      {(!sshAccess || sshAccess.length === 0) && <EnableSSHBanner />}
      {sshAccess && sshAccess.length > 0 && (
        <>
          <NodeDetailSectionHeader
            trailing={
              <Button
                variant="primary"
                onClick={() => setAddLinuxUserOpen(true)}
                className="flex items-center gap-1.5"
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                Add Linux User
              </Button>
            }
          ></NodeDetailSectionHeader>
          <AddLinuxUserDialog
            open={addLinuxUserOpen}
            onOpenChange={setAddLinuxUserOpen}
          />
          <div className="mb-3 w-full rounded-md bg-secondaryBg p-4">
            <NodeDetailSectionDescription className="mb-4">
              Manage SSH access for your organization members
            </NodeDetailSectionDescription>
            {members.length > 0 && (
              <div className="mb-2">
                <Input
                  type="search"
                  placeholder="Search by username, email, or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="max-w-full"
                />
              </div>
            )}
            {loadingMembers ? (
              <div className="mt-5 text-center py-4">
                <span className="text-sm text-gray-700 dark:text-secondary">
                  Loading members…
                </span>
              </div>
            ) : members.length === 0 ? (
              <div className="mt-5 text-center py-4">
                <span className="text-sm text-gray-700 dark:text-secondary">
                  No one else is in the organization.
                </span>
              </div>
            ) : (
              <div className="mt-2">
                <TableRoot>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell>User</TableHeaderCell>
                        <TableHeaderCell>Linux Users</TableHeaderCell>
                        {canManage && <TableHeaderCell>Manage</TableHeaderCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredMembers.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={canManage ? 3 : 2}>
                            <div className="text-center py-4">
                              <span className="text-sm text-gray-700 dark:text-secondary">
                                No users found matching &ldquo;{searchTerm}
                                &rdquo;
                              </span>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredMembers.map(({ userId }) => {
                          const userLinuxUsers = accessByUser[userId] ?? [];
                          return (
                            <TableRow key={userId}>
                              <TableCell>
                                <div className="flex flex-row items-center">
                                  <UsernameDisplayCircle userId={userId} />
                                  <UsernameDisplayField
                                    userId={userId}
                                    className="text-gray-900 dark:text-white ml-1 font-medium"
                                  />
                                  <div className="text-gray-900 dark:text-white">
                                    &nbsp;•&nbsp;
                                  </div>
                                  <EmailDisplayField
                                    userId={userId}
                                    className="text-gray-900 dark:text-white text-xs"
                                  />
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-wrap gap-1">
                                  {userLinuxUsers.length > 0 ? (
                                    userLinuxUsers.map((lu) => (
                                      <span
                                        key={lu}
                                        className="inline-flex items-center rounded px-2 py-0.5 text-xs font-mono bg-neutral-800 text-neutral-200"
                                      >
                                        {lu}
                                      </span>
                                    ))
                                  ) : (
                                    <span className="text-sm text-neutral-500">
                                      —
                                    </span>
                                  )}
                                </div>
                              </TableCell>
                              {canManage && (
                                <TableCell>
                                  {myLinuxUsers.length > 0 && (
                                    <Button
                                      variant="secondary"
                                      onClick={() => setModifyingUserId(userId)}
                                      isLoading={loadingUsers.has(userId)}
                                      disabled={loadingUsers.size > 0}
                                    >
                                      Modify Access
                                    </Button>
                                  )}
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableRoot>
              </div>
            )}
          </div>

          <ModifySSHAccessDialog
            open={modifyingUserId !== null}
            onOpenChange={(open) => {
              if (!open) setModifyingUserId(null);
            }}
            manageableLinuxUsers={myLinuxUsers}
            readOnlyLinuxUsers={(
              accessByUser[modifyingUserId ?? ""] ?? []
            ).filter((lu) => !myLinuxUsers.includes(lu))}
            currentLinuxUsers={(
              accessByUser[modifyingUserId ?? ""] ?? []
            ).filter((lu) => myLinuxUsers.includes(lu))}
            onSave={handleModifyAccess}
          />
        </>
      )}
    </>
  );
};
